/**
 * Typed t() wrapper
 *
 * Provides compile-time key checking and DEV missing-key warnings.
 *
 * Usage:
 *   import { useT } from '@/lib/t-wrapper'
 *   const { t } = useT()
 *   t('mori.listening')          // ✓ autocompleted, type-checked
 *   t('mori.typo_here')          // ✗ TypeScript error
 *
 * Existing code using `useTranslation()` directly continues to work — no migration required.
 */

import { useTranslation } from 'react-i18next'
import type enJson from '@/locales/en.json'

// ── Type derivation ───────────────────────────────────────────────────────────
// Recursively flattens a nested object type to all possible dotted-key paths.
type FlattenKeys<T, Prefix extends string = ''>
  = T extends Record<string, unknown>
    ? { [K in keyof T & string]:
        T[K] extends Record<string, unknown>
          ? FlattenKeys<T[K], Prefix extends '' ? K : `${Prefix}.${K}`>
          : Prefix extends '' ? K : `${Prefix}.${K}`
      }[keyof T & string]
    : never

export type TranslationKey = FlattenKeys<typeof enJson>

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useT() {
  const { t: rawT, i18n } = useTranslation()

  const t = (key: TranslationKey, options?: Record<string, unknown>): string => {
    const result = rawT(key as string, options)
    if (import.meta.env.DEV && result === key) {
      console.warn(`[i18n] Missing translation key: "${key}"`)
    }
    return result
  }

  return { t, i18n }
}
